import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import I3PageHero from '../components/I3PageHero';
import { useRevealSections } from '../hooks/useReveal';
import {
  estimateReadMinutes,
  getI3BlogPostsSorted,
  isI3BlogQuoteOnly,
  type I3BlogPost,
} from '../data/i3BlogPosts';
import { useI3SiteContent } from '../cms/useI3SiteContent';
import { mergeBlogPostsForIndex } from '../cms/blogVisibility';
import {
  I3_BLOG_FORMAT_LABELS,
  type I3BlogFormat,
  resolveBlogFormat,
} from '../cms/blogFormat';
import '../styles/I3Page.css';
import '../styles/I3Reportage.css';
import '../styles/I3Blog.css';

/** Top row: four most recent posts, left to right. */
const RECENT_ROW_COUNT = 4;

function formatPostDate(iso: string): string {
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function authorInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ALL_TOPICS = 'all' as const;
const ALL_AUTHORS = 'all' as const;
const ALL_FORMATS = 'all' as const;

const FORMAT_OPTIONS: I3BlogFormat[] = ['daily', 'devotional', 'article'];

function BlogPostCard({
  post,
  onPreview,
}: {
  post: I3BlogPost;
  onPreview: (p: I3BlogPost) => void;
}) {
  const quoteOnly = isI3BlogQuoteOnly(post);
  const format = resolveBlogFormat(post);

  return (
    <article className={`i3-blog-card i3-blog-card--recent${quoteOnly ? ' i3-blog-card--quote-only' : ''}`}>
      <div className="i3-blog-card-top">
        <div className="i3-blog-card-pills">
          <span className="i3-blog-format-pill">{I3_BLOG_FORMAT_LABELS[format]}</span>
          <span className="i3-blog-topic-pill">
            {post.topic?.trim() || 'Journal'}
          </span>
        </div>
        <span className="i3-blog-card-meta-inline">
          {formatPostDate(post.publishedAt)} · {estimateReadMinutes(post)} min
        </span>
      </div>
      <h3 className="i3-blog-card-title">
        {quoteOnly ? (
          <button
            type="button"
            className="i3-blog-card-title-btn"
            onClick={() => onPreview(post)}
          >
            {post.title}
          </button>
        ) : (
          <Link to={`/blog/${post.slug}`}>{post.title}</Link>
        )}
      </h3>
      <p className="i3-blog-card-excerpt">{post.excerpt}</p>
      <div className="i3-blog-card-bottom">
        <div className="i3-blog-card-author">
          <span className="i3-blog-avatar i3-blog-avatar-sm" aria-hidden="true">
            {authorInitials(post.author)}
          </span>
          <span className="i3-blog-author-name">{post.author}</span>
        </div>
        <div className="i3-blog-card-actions">
          <button
            type="button"
            className={quoteOnly ? 'i3-blog-linkish i3-blog-linkish-strong' : 'i3-blog-linkish'}
            onClick={() => onPreview(post)}
          >
            {quoteOnly ? 'Read reflection' : 'Quick read'}
          </button>
          {!quoteOnly ? (
            <Link
              to={`/blog/${post.slug}`}
              className="i3-blog-linkish i3-blog-linkish-strong"
            >
              Full article →
            </Link>
          ) : null}
        </div>
      </div>
    </article>
  );
}

const I3Blog: React.FC = () => {
  const { content, loading, error, ready } = useI3SiteContent();
  const posts = useMemo(
    () => mergeBlogPostsForIndex(content.blogPosts, getI3BlogPostsSorted()),
    [content.blogPosts]
  );
  const [query, setQuery] = useState('');
  const [topicFilter, setTopicFilter] = useState<string>(ALL_TOPICS);
  const [authorFilter, setAuthorFilter] = useState<string>(ALL_AUTHORS);
  const [formatFilter, setFormatFilter] = useState<string>(ALL_FORMATS);
  const [previewPost, setPreviewPost] = useState<I3BlogPost | null>(null);
  const previewRef = useRef<HTMLDialogElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  useRevealSections(wrapRef);

  const topicOptions = useMemo(() => {
    const s = new Set<string>();
    posts.forEach((p) => {
      if (p.topic?.trim()) s.add(p.topic.trim());
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [posts]);

  const authorOptions = useMemo(() => {
    const s = new Set<string>();
    posts.forEach((p) => {
      if (p.author?.trim()) s.add(p.author.trim());
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [posts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      const topic = p.topic?.trim() || 'Journal';
      const author = p.author?.trim() || '';
      const format = resolveBlogFormat(p);
      const matchTopic = topicFilter === ALL_TOPICS || topic === topicFilter;
      const matchAuthor = authorFilter === ALL_AUTHORS || author === authorFilter;
      const matchFormat = formatFilter === ALL_FORMATS || format === formatFilter;
      if (!matchTopic || !matchAuthor || !matchFormat) return false;
      if (!q) return true;
      const hay = `${p.title} ${p.excerpt} ${p.author} ${topic}`.toLowerCase();
      return hay.includes(q);
    });
  }, [posts, query, topicFilter, authorFilter, formatFilter]);

  const recentPosts = useMemo(
    () => filtered.slice(0, RECENT_ROW_COUNT),
    [filtered]
  );

  const gridPosts = useMemo(() => filtered.slice(RECENT_ROW_COUNT), [filtered]);

  useEffect(() => {
    const d = previewRef.current;
    if (!d) return;
    if (previewPost) {
      if (!d.open) d.showModal();
    } else if (d.open) {
      d.close();
    }
  }, [previewPost]);

  return (
    <article className="i3-page i3-reportage-page i3-blog-page">
      <I3PageHero
        showLogo
        title="Journal"
        subtitle={
          posts.length > 0
            ? `Stories on faith, relationships, and leadership · ${posts.length} articles`
            : 'Insights, encouragement, and updates from I-3'
        }
      />
      <div className="i3-page-wrap i3-blog-hub" ref={wrapRef}>
        {loading && !ready && (
          <p className="i3-cms-loading">Loading journal from CMS…</p>
        )}
        {error && ready && (
          <p className="i3-cms-error" role="status">
            Using offline content (CMS fetch failed). {error.message}
          </p>
        )}

        <header className="i3-blog-hub-head i3-reveal">
          <p className="i3-blog-hub-lead">
            Filter by format, topic, or author — or search what you need. Open a{' '}
            <strong>quick read</strong> for daily reflections, or dive into a full devotional or article.
          </p>
          <div className="i3-blog-hub-stats" aria-hidden="true">
            <div className="i3-blog-stat">
              <span className="i3-blog-stat-value">{posts.length}</span>
              <span className="i3-blog-stat-label">Articles</span>
            </div>
            <div className="i3-blog-stat">
              <span className="i3-blog-stat-value">{topicOptions.length}</span>
              <span className="i3-blog-stat-label">Topics</span>
            </div>
            <div className="i3-blog-stat">
              <span className="i3-blog-stat-value">{filtered.length}</span>
              <span className="i3-blog-stat-label">Showing</span>
            </div>
          </div>
        </header>

        <div className="i3-blog-toolbar i3-reveal">
          <div className="i3-blog-toolbar-search-wrap">
            <label className="i3-blog-search" htmlFor="i3-blog-search-input">
              <span className="i3-blog-search-label">Search</span>
              <input
                id="i3-blog-search-input"
                type="search"
                className="i3-blog-search-input"
                placeholder="Title, author, topic…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
              />
            </label>
          </div>
          <div className="i3-blog-toolbar-selects" role="group" aria-label="Filter articles">
            <label className="i3-blog-filter-field">
              <span className="i3-blog-filter-label">Format</span>
              <select
                className="i3-blog-select"
                value={formatFilter}
                onChange={(e) => setFormatFilter(e.target.value)}
                aria-label="Filter by format"
              >
                <option value={ALL_FORMATS}>All formats</option>
                {FORMAT_OPTIONS.map((f) => (
                  <option key={f} value={f}>
                    {I3_BLOG_FORMAT_LABELS[f]}
                  </option>
                ))}
              </select>
            </label>
            <label className="i3-blog-filter-field">
              <span className="i3-blog-filter-label">Topic</span>
              <select
                className="i3-blog-select"
                value={topicFilter}
                onChange={(e) => setTopicFilter(e.target.value)}
                aria-label="Filter by topic"
              >
                <option value={ALL_TOPICS}>All topics</option>
                {topicOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="i3-blog-filter-field">
              <span className="i3-blog-filter-label">Author</span>
              <select
                className="i3-blog-select"
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
                aria-label="Filter by author"
              >
                <option value={ALL_AUTHORS}>All authors</option>
                {authorOptions.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="i3-blog-empty i3-reveal" role="status">
            No articles match your filters.{' '}
            <button
              type="button"
              className="i3-blog-reset-filters"
              onClick={() => {
                setQuery('');
                setTopicFilter(ALL_TOPICS);
                setAuthorFilter(ALL_AUTHORS);
                setFormatFilter(ALL_FORMATS);
              }}
            >
              Clear search and filters
            </button>
          </p>
        ) : (
          <>
            {recentPosts.length > 0 ? (
              <section className="i3-blog-recent-section i3-reveal" aria-labelledby="i3-blog-recent-heading">
                <h2 id="i3-blog-recent-heading" className="i3-blog-section-title">
                  Latest
                </h2>
                <ul className="i3-blog-recent">
                  {recentPosts.map((post) => (
                    <li key={post.slug} className="i3-blog-recent-item">
                      <BlogPostCard post={post} onPreview={setPreviewPost} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {gridPosts.length > 0 ? (
              <section className="i3-blog-more-section i3-reveal" aria-labelledby="i3-blog-more-heading">
                <h2 id="i3-blog-more-heading" className="i3-blog-section-title">
                  More articles
                </h2>
                <ul className="i3-blog-grid">
                  {gridPosts.map((post) => (
                    <li key={post.slug} className="i3-blog-grid-item">
                      <BlogPostCard post={post} onPreview={setPreviewPost} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        )}
      </div>

      <dialog
        ref={previewRef}
        className="i3-blog-preview-dialog"
        onClose={() => setPreviewPost(null)}
      >
        {previewPost ? (() => {
          const quoteOnly = isI3BlogQuoteOnly(previewPost);
          return (
          <div className={`i3-blog-preview-inner${quoteOnly ? ' i3-blog-preview-inner--quote-only' : ''}`}>
            <button
              type="button"
              className="i3-blog-preview-close"
              aria-label="Close preview"
              onClick={() => setPreviewPost(null)}
            >
              ×
            </button>
            <span className="i3-blog-format-pill i3-blog-format-pill-sm">
              {I3_BLOG_FORMAT_LABELS[resolveBlogFormat(previewPost)]}
            </span>
            <span className="i3-blog-topic-pill">{previewPost.topic?.trim() || 'Journal'}</span>
            <h2 className="i3-blog-preview-title">{previewPost.title}</h2>
            <p className="i3-blog-preview-meta">
              {formatPostDate(previewPost.publishedAt)} · {previewPost.author} ·{' '}
              {estimateReadMinutes(previewPost)} min read
            </p>
            {!quoteOnly ? (
              <p className="i3-blog-preview-excerpt">{previewPost.excerpt}</p>
            ) : null}
            {previewPost.quote?.trim() ? (
              <blockquote className="i3-blog-preview-quote">“{previewPost.quote.trim()}”</blockquote>
            ) : null}
            {!quoteOnly ? (
              <div className="i3-blog-preview-body">
                {previewPost.quote?.trim() && previewPost.meaning?.trim() ? (
                  <p className="i3-blog-preview-p">
                    <strong>Meaning:</strong> {previewPost.meaning.trim()}
                  </p>
                ) : (
                  previewPost.paragraphs.slice(0, 2).map((p, i) => (
                    <p key={i} className="i3-blog-preview-p">
                      {p}
                    </p>
                  ))
                )}
              </div>
            ) : null}
            {!quoteOnly ? <p className="i3-blog-preview-more">…</p> : null}
            <div className="i3-blog-preview-actions">
              <button
                type="button"
                className={quoteOnly ? 'i3-blog-btn i3-blog-btn-primary' : 'i3-blog-btn i3-blog-btn-ghost'}
                onClick={() => setPreviewPost(null)}
              >
                Close
              </button>
              {!quoteOnly ? (
                <Link
                  to={`/blog/${previewPost.slug}`}
                  className="i3-blog-btn i3-blog-btn-primary"
                  onClick={() => setPreviewPost(null)}
                >
                  Open full article
                </Link>
              ) : null}
            </div>
          </div>
          );
        })() : null}
      </dialog>
    </article>
  );
};

export default I3Blog;
